import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

const AGE_VERIFIED_KEY = 'verbi_age_verified';

const AgeGate = ({ children }) => {
    const [isVerified, setIsVerified] = useState(null); // null = loading, true = verified, false = denied
    const [showDenied, setShowDenied] = useState(false);

    useEffect(() => {
        const verified = localStorage.getItem(AGE_VERIFIED_KEY);
        if (verified === 'true') {
            setIsVerified(true);
        } else {
            setIsVerified(false);
        }
    }, []);

    const handleConfirm = () => {
        localStorage.setItem(AGE_VERIFIED_KEY, 'true');
        setIsVerified(true);
    };

    const handleDeny = () => {
        // Don't persist denial - just show the denied screen temporarily
        setShowDenied(true);
    };

    const handleRetry = () => {
        // Allow user to go back and try again (in case of accidental tap)
        setShowDenied(false);
    };

    // Still checking localStorage
    if (isVerified === null) {
        return null;
    }

    // User confirmed they're 18+
    if (isVerified === true) {
        return children;
    }

    // User said they're under 18
    if (showDenied) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-3">
                        Access Restricted
                    </h1>
                    <p className="text-slate-600 text-sm mb-6">
                        Verbi is only available to users 18 years of age or older.
                    </p>
                    <button
                        onClick={handleRetry}
                        className="w-full bg-slate-100 text-slate-700 font-bold py-3 px-6 rounded-xl uppercase tracking-wider hover:bg-slate-200 transition-all active:scale-95 text-xs"
                    >
                        Go Back
                    </button>
                    <p className="text-[10px] text-slate-400 mt-4">
                        Tapped by accident? Press "Go Back" to try again.
                    </p>
                </div>
            </div>
        );
    }

    // Age verification prompt
    return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
                {/* Logo/Icon */}
                <div className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <ShieldCheckIcon className="w-10 h-10 text-white" />
                </div>

                {/* Title */}
                <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-2">
                    Age Verification
                </h1>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-6">
                    Required to Continue
                </p>

                {/* Content */}
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                    <p className="text-slate-700 text-sm leading-relaxed mb-3">
                        Verbi contains user-generated content including discussions on mature topics
                        such as politics, social issues, and current events.
                    </p>
                    <p className="text-slate-700 text-sm leading-relaxed">
                        By continuing, you confirm that you are <span className="font-bold">18 years of age or older</span> and
                        agree to our Terms of Service and Community Guidelines.
                    </p>
                </div>

                {/* Buttons */}
                <div className="space-y-3">
                    <button
                        onClick={handleConfirm}
                        className="w-full bg-slate-900 text-white font-bold py-4 px-6 rounded-xl uppercase tracking-wider hover:bg-slate-800 transition-all active:scale-95 text-sm"
                    >
                        I am 18 or Older — Enter
                    </button>
                    <button
                        onClick={handleDeny}
                        className="w-full bg-slate-100 text-slate-600 font-bold py-3 px-6 rounded-xl uppercase tracking-wider hover:bg-slate-200 transition-all active:scale-95 text-xs"
                    >
                        I am Under 18
                    </button>
                </div>

                {/* Footer */}
                <p className="text-[10px] text-slate-400 mt-6 leading-relaxed">
                    This verification is required for compliance with international app store regulations.
                    Your age confirmation is stored locally on your device.
                </p>
            </div>
        </div>
    );
};

export default AgeGate;
